import { apiGet, apiClient } from './client';
import type {
  SectorIntelFilters,
  SectorIntelFilterOptions,
  SectorIntelListResponse,
  SectorGroupsListResponse,
} from '../pages/SectorIntelligence/sectorIntel.types';

// 권위: dashboard-api SectorIntelligenceController
//   GET /api/v1/sector-intelligence                 종목 단위 목록
//   GET /api/v1/sector-intelligence/filter-options  소스/섹터유형 옵션
//   GET /api/v1/sector-intelligence/export          종목 단위 xlsx
//   GET /api/v1/sector-intelligence/sectors         섹터 단위(집계) 목록
//   GET /api/v1/sector-intelligence/sectors/export  섹터 단위 xlsx
//
// `/api/v1/sectors/history` 는 화면 17 LeadingSectorsPage 의 leadingSectorsApi.ts 가 권위.

const BASE = '/api/v1/sector-intelligence';

function buildListParams(f: SectorIntelFilters): URLSearchParams {
  const p = new URLSearchParams();
  p.set('page', String(Math.max(0, f.page)));
  p.set('size', String(Math.max(1, Math.min(500, f.size))));
  if (f.sectorTypes.length > 0) p.set('sector_type', f.sectorTypes.join(','));
  if (f.sources.length > 0) p.set('source', f.sources.join(','));
  if (f.sectorName.trim()) p.set('sector_name', f.sectorName.trim());
  if (f.stockName?.trim()) p.set('stock_name', f.stockName.trim());
  if (f.canonicalSectorId?.trim()) {
    p.set('canonical_sector_id', f.canonicalSectorId.trim());
  }
  if (f.activeOnly) p.set('active_only', 'true');
  return p;
}

export async function getSectorIntelList(
  filters: SectorIntelFilters
): Promise<SectorIntelListResponse> {
  const qs = buildListParams(filters).toString();
  return apiGet<SectorIntelListResponse>(`${BASE}?${qs}`);
}

export async function getSectorIntelFilterOptions(): Promise<SectorIntelFilterOptions> {
  return apiGet<SectorIntelFilterOptions>(`${BASE}/filter-options`);
}

/**
 * 엑셀 다운로드 — blob 응답을 받아 브라우저에서 파일 저장 트리거.
 * envelope interceptor 는 blob 응답이라 언랩을 건너뛰지만, 서버가 에러 발생 시 JSON envelope 를 내리므로
 * 캐치에서 blob→JSON 변환 시도.
 */
export async function downloadSectorIntelExport(
  filters: SectorIntelFilters
): Promise<void> {
  const qs = buildListParams(filters).toString();
  try {
    const resp = await apiClient.get(`${BASE}/export?${qs}`, {
      responseType: 'blob',
      headers: {
        Accept:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/octet-stream',
      },
    });

    const blob = resp.data as Blob;
    const cd = (resp.headers?.['content-disposition'] ?? resp.headers?.['Content-Disposition']) as
      | string
      | undefined;
    const filename = parseFilename(cd) ?? defaultFilename();

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (err: unknown) {
    // blob 응답 중 서버가 JSON 에러를 내린 경우 → 수동 파싱.
    const maybe = err as {
      response?: { data?: Blob | unknown; status?: number };
      code?: string;
      message?: string;
    };
    const data = maybe.response?.data;
    if (data instanceof Blob) {
      const text = await data.text();
      try {
        const parsed = JSON.parse(text) as {
          success?: boolean;
          error?: { code?: string; message?: string };
        };
        const code = parsed.error?.code ?? 'export_failed';
        const message = parsed.error?.message ?? '다운로드에 실패했습니다.';
        throw new Error(`${code}:${message}`);
      } catch {
        throw new Error('export_failed:다운로드에 실패했습니다.');
      }
    }
    throw err instanceof Error ? err : new Error('export_failed');
  }
}

function parseFilename(contentDisposition: string | undefined): string | null {
  if (!contentDisposition) return null;
  // filename*=UTF-8''... 또는 filename="..." 형태
  const utfMatch = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utfMatch) {
    try {
      return decodeURIComponent(utfMatch[1].trim().replace(/^"|"$/g, ''));
    } catch {
      // fallthrough
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return plain ? plain[1].trim() : null;
}

function defaultFilename(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `sector-intelligence-${y}${m}${d}.xlsx`;
}

function defaultGroupsFilename(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `sector-groups-${y}${m}${d}.xlsx`;
}

// ── 섹터 단위(집계) ────────────────────────────────────────────────

export async function getSectorGroups(
  filters: SectorIntelFilters
): Promise<SectorGroupsListResponse> {
  const qs = buildListParams(filters).toString();
  return apiGet<SectorGroupsListResponse>(`${BASE}/sectors?${qs}`);
}

export async function downloadSectorGroupsExport(
  filters: SectorIntelFilters
): Promise<void> {
  const qs = buildListParams(filters).toString();
  try {
    const resp = await apiClient.get(`${BASE}/sectors/export?${qs}`, {
      responseType: 'blob',
      headers: {
        Accept:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/octet-stream',
      },
    });

    const blob = resp.data as Blob;
    const cd = (resp.headers?.['content-disposition'] ?? resp.headers?.['Content-Disposition']) as
      | string
      | undefined;
    const filename = parseFilename(cd) ?? defaultGroupsFilename();

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (err: unknown) {
    const maybe = err as {
      response?: { data?: Blob | unknown; status?: number };
      code?: string;
      message?: string;
    };
    const data = maybe.response?.data;
    if (data instanceof Blob) {
      const text = await data.text();
      try {
        const parsed = JSON.parse(text) as {
          success?: boolean;
          error?: { code?: string; message?: string };
        };
        const code = parsed.error?.code ?? 'export_failed';
        const message = parsed.error?.message ?? '다운로드에 실패했습니다.';
        throw new Error(`${code}:${message}`);
      } catch {
        throw new Error('export_failed:다운로드에 실패했습니다.');
      }
    }
    throw err instanceof Error ? err : new Error('export_failed');
  }
}
